/**
 * Groups service — scope/role permission editor.
 * Ported 1:1 from the legacy flat services/groups.js. Response shapes verified
 * against docs/groups-migration/Api-response/.
 */
import { getChatApiBase, getChatApiHeaders } from '../chatApiClient'
import axios from '../axiosInstance'
import {
  ChatApp,
  ScopePermissionsListResponse,
  ScopePermissionsValuesResponse,
  ResetScopePermissionResponse,
} from './types'

/** Full permission definitions (metadata + current value) for rendering an
 *  editor UI — use `getGroupScopePermissionsValues` for a cheaper read when
 *  only the current values are needed. */
export async function listGroupScopePermissions(
  guid: string,
  scope: string,
  app: ChatApp,
  apiHost: string,
): Promise<ScopePermissionsListResponse> {
  const URL = `${getChatApiBase(
    app,
    apiHost,
  )}/groups/${guid}/scopes/${scope}/permissions?includePermissionObject=true`

  return axios
    .get(`${URL}`, {
      headers: getChatApiHeaders(app),
      params: { _: Date.now() },
    })
    .then(res => res.data)
    .catch(error => (error.response ? error.response.data : error))
}

export async function getGroupScopePermissionsValues(
  guid: string,
  scope: string,
  app: ChatApp,
  apiHost: string,
): Promise<ScopePermissionsValuesResponse> {
  const URL = `${getChatApiBase(app, apiHost)}/groups/${guid}/scopes/${scope}/permissions`
  return axios
    .get(`${URL}`, {
      headers: getChatApiHeaders(app),
      params: { _: Date.now() },
    })
    .then(res => res.data)
    .catch(error => (error.response ? error.response.data : error))
}

/** Verified: a partial update (e.g. one changed key) returns the FULL
 *  resulting permissions map, not just the changed key. */
export async function updateGroupScopePermissions(
  guid: string,
  scope: string,
  post: { permissions: Record<string, string | string[]> },
  app: ChatApp,
  apiHost: string,
): Promise<ScopePermissionsValuesResponse> {
  const URL = `${getChatApiBase(app, apiHost)}/groups/${guid}/scopes/${scope}/permissions`
  return axios
    .put(`${URL}`, post, {
      headers: getChatApiHeaders(app),
    })
    .then(res => res.data)
    .catch(error => (error.response ? error.response.data : error))
}

/** Verified: this can return a real `ERR_SCOPE_PERMISSION_NOT_MAPPED` error —
 *  reset only succeeds on a permission that's already been custom-mapped as a
 *  group-level override for that scope, not one still at its inherited
 *  default. Callers must handle the error response, not assume success. */
export async function resetGroupScopePermission(
  guid: string,
  scope: string,
  permissionId: string,
  app: ChatApp,
  apiHost: string,
): Promise<ResetScopePermissionResponse> {
  const URL = `${getChatApiBase(
    app,
    apiHost,
  )}/groups/${guid}/scopes/${scope}/permissions/${permissionId}`
  return axios
    .delete(`${URL}`, {
      headers: getChatApiHeaders(app),
    })
    .then(res => res.data)
    .catch(error => (error.response ? error.response.data : error))
}
